import * as t from 'io-ts';
import _, {isNull} from 'lodash';

import {makeApiType, makeEnumRuntimeType} from './apiTypes';
import {CrmHubspotFieldModel} from './crmHubspotFieldModel';
import {CrmSalesforceFieldModel} from './crmSalesforceFieldModel';
import {
  IntegrationModel,
  IntegrationSettingsTypesEnum,
  parseIntegration,
  RIntegration,
} from './integrationModel';
import {parseJob, RJob} from './jobModel';

/*
 * Constants.
 */

export enum CrmSyncIntegrationResourceTypesEnum {
  ACCOUNT = 'account',
  CARD = 'card',
}

export enum CrmSyncSourcesEnum {
  SALESFORCE = 'salesforce',
  HUBSPOT = 'hubspot',
  MICROSOFT_DYNAMICS = 'microsoftdynamics',
  UNKNOWN = 'unknown',
}

export enum CrmSyncStatusesEnum {
  ENABLED = 'enabled',
  DISABLED = 'disabled',
}

export enum CrmSyncErrorCodesEnum {
  CRM_FIELD_NOT_FOUND = 'crm_field_not_found',
  INVALID_DOMAIN = 'invalid_domain',
  INVALID_HANDLE = 'invalid_handle',
  HANDLE_ALREADY_TAKEN = 'handle_already_taken',
  COMMON_DOMAIN_USED = 'common_domain_used',
  NAME_ALREADY_TAKEN = 'name_already_taken',
  DOMAIN_ALREADY_TAKEN = 'domain_already_taken',
  NAME_NOT_FOUND = 'name_not_found',
  UNKNOWN_ERROR = 'unknown_error',
}

/**
 * The AccountModel has fields that can be mapped to that are not true Custom Fields.
 * We use negative numbers to represent those fields in the mapping object of the Sync model.
 */
export enum CrmSyncDefaultFieldMappingIdsEnum {
  NAME = -1,
  DOMAINS = -2,
  DESCRIPTION = -3,
}

export enum CrmSyncMappingCustomFieldTypesEnum {
  DESCRIPTION = 'account.description',
  DOMAINS = 'account.domains',
  NAME = 'account.name',
}

const crmSyncSourceToIntegrationSource: {
  [source in CrmSyncSourcesEnum]: IntegrationSettingsTypesEnum | undefined;
} = {
  [CrmSyncSourcesEnum.SALESFORCE]: IntegrationSettingsTypesEnum.SALESFORCE,
  [CrmSyncSourcesEnum.HUBSPOT]: IntegrationSettingsTypesEnum.HUBSPOT,
  [CrmSyncSourcesEnum.MICROSOFT_DYNAMICS]: IntegrationSettingsTypesEnum.MICROSOFT_DYNAMICS,
  [CrmSyncSourcesEnum.UNKNOWN]: undefined,
};

/*
 * Runtime type.
 */

const RCrmSyncMetadata = t.type({
  externalListId: t.union([t.string, t.undefined]),
});

const RCrmSyncError = t.type({
  code: t.union([makeEnumRuntimeType(CrmSyncErrorCodesEnum), t.string]),
  timestamp: t.union([t.undefined, t.number]),
  externalResourceId: t.union([t.undefined, t.string]),
  externalResourceName: t.union([t.undefined, t.string]),
  reason: t.union([t.undefined, t.string]),
  externalFieldId: t.union([t.undefined, t.string]),
  frontFieldId: t.union([t.undefined, t.number]),
});

const RCrmSyncDirty = t.readonly(
  t.type({
    id: t.Integer,
    integrationId: t.Integer,
    resourceType: makeEnumRuntimeType(CrmSyncIntegrationResourceTypesEnum),
    source: makeEnumRuntimeType(CrmSyncSourcesEnum),
    status: makeEnumRuntimeType(CrmSyncStatusesEnum),
    externalUrl: t.union([t.string, t.undefined]),
    // Maps a crm source to front custom field id
    mapping: t.record(t.string, t.Integer),
    metadata: RCrmSyncMetadata,
    createdAt: t.Integer,
    updatedAt: t.Integer,
    lastSyncedAt: t.Integer,
    errors: t.readonlyArray(RCrmSyncError),
  }),
);

/*
 * Type.
 */

export interface CrmSyncModel extends t.TypeOf<typeof RCrmSyncDirty> {}
export interface CrmSyncMetadataModel extends t.TypeOf<typeof RCrmSyncMetadata> {}
export interface CrmSyncsModel extends t.TypeOf<typeof RCrmSyncs> {}
export interface CrmSyncErrorModel extends t.TypeOf<typeof RCrmSyncError> {}

export const RCrmSync = t.clean<CrmSyncModel>(RCrmSyncDirty as any);
export const RCrmSyncDetails = t.type({
  crmSync: RCrmSync,
  integration: RIntegration,
  job: t.union([RJob, t.undefined]),
});
export const RCrmSyncs = t.type({
  crmSyncs: t.readonlyArray(RCrmSync),
  integrations: t.readonlyArray(RIntegration),
  jobs: t.readonlyArray(RJob),
});

export type CrmFieldModel = CrmSalesforceFieldModel | CrmHubspotFieldModel;

/*
 * IO methods.
 */

export function parseCrmSync(src: any) {
  const integration = parseIntegration(src.integration);
  const job = !isNull(src.job) ? parseJob(src.job) : undefined;
  return {
    crmSync: {
      id: src.id,
      integrationId: src.integration.id,
      resourceType: src.resource_type,
      externalUrl: src.base_external_url || undefined,
      source: parseCrmSyncSourceFromIntegration(integration),
      status: src.status,
      mapping: src.mapping || {},
      metadata: parseCrmSyncMetadata(src.metadata),
      createdAt: src.updated_at || 0,
      updatedAt: src.created_at || 0,
      lastSyncedAt: src.job?.last_batch_started_at || src.job?.created_at || 0,
      errors: parseCrmSyncErrors(src.errors),
    },
    integration,
    job,
  };
}

export function mapCrmSyncSourceToIntegrationType(source: CrmSyncSourcesEnum) {
  return crmSyncSourceToIntegrationSource[source];
}

function parseCrmSyncSourceFromIntegration(integration: IntegrationModel) {
  switch (integration.type) {
    case IntegrationSettingsTypesEnum.SALESFORCE:
      return CrmSyncSourcesEnum.SALESFORCE;
    case IntegrationSettingsTypesEnum.HUBSPOT:
      return CrmSyncSourcesEnum.HUBSPOT;
    case IntegrationSettingsTypesEnum.MICROSOFT_DYNAMICS:
      return CrmSyncSourcesEnum.MICROSOFT_DYNAMICS;
    default:
      return CrmSyncSourcesEnum.UNKNOWN;
  }
}

function parseCrmSyncErrors(errors: any) {
  if (!errors) {
    return [];
  }
  return errors.map((error: any) => ({
    code: error.code,
    timestamp: error.timestamp,
    reason: error.reason,
    externalResourceId: error.external_resource_id,
    externalResourceName: error.external_resource_name,
    externalFieldId: error.external_field_id,
    frontFieldId: error.front_field_id,
  }));
}

function parseCrmSyncMetadata(metadata: any) {
  return {
    externalListId: metadata.external_list_id || undefined,
  };
}

function parseCrmSyncs(src: any): CrmSyncsModel {
  return src.syncs.reduce(
    (data: CrmSyncsModel, rawCrmSyncData: any) => {
      const {crmSync, integration, job} = parseCrmSync(rawCrmSyncData);

      return {
        crmSyncs: [...data.crmSyncs, crmSync],
        integrations: [...data.integrations, integration],
        jobs: _([...data.jobs, job])
          .compact()
          .value(),
      };
    },
    {crmSyncs: [], integrations: [], jobs: []},
  );
}

/*
 * API type.
 */

export const ApiCrmSync = makeApiType('crmSync', RCrmSyncDetails, parseCrmSync);
export const ApiCrmSyncs = makeApiType('crmSyncs', RCrmSyncs, parseCrmSyncs);
